import React, { useEffect } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';
import { useLocation } from 'react-router-dom';

// Tracks pathnames whose Springs have animated this session.
// Subsequent visits to the same path skip the entrance animation
// so SPA navigation back to a previously-visited route doesn't read as "loading".
const seenPaths = new Set<string>();

interface SpringProps {
  children: React.ReactNode;
  index?: number;
  className?: string;
  type?: 'fade' | 'slideUp' | 'slideLeft' | 'zoom';
}

export const Spring: React.FC<SpringProps> = ({
  children,
  index = 1,
  className = '',
  type = 'fade',
}) => {
  const location = useLocation();
  const skip = seenPaths.has(location.pathname);

  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) seenPaths.add(location.pathname);
  }, [inView, location.pathname]);

  const visible = skip || inView;

  const commonProps = {
    config: { duration: 300 },
    delay: skip ? 0 : 100 * index,
  };

  const fade = useSpring({
    from: { opacity: skip ? 1 : 0 },
    to: { opacity: visible ? 1 : 0 },
    ...commonProps,
  });

  const slideLeft = useSpring({
    from: { transform: skip ? 'translateX(0)' : 'translateX(50px)', opacity: skip ? 1 : 0 },
    to: { transform: visible ? 'translateX(0)' : 'translateX(50px)', opacity: visible ? 1 : 0 },
    ...commonProps,
  });

  const slideUp = useSpring({
    from: { opacity: skip ? 1 : 0, transform: skip ? 'translateY(0px)' : 'translateY(40px)' },
    to: { opacity: visible ? 1 : 0, transform: visible ? 'translateY(0px)' : 'translateY(40px)' },
    ...commonProps,
  });

  const zoom = useSpring({
    from: { opacity: skip ? 1 : 0, transform: skip ? 'scale(1)' : 'scale(0.5)' },
    to: { opacity: visible ? 1 : 0, transform: visible ? 'scale(1)' : 'scale(0.5)' },
    ...commonProps,
  });

  const springs = { fade, slideLeft, slideUp, zoom };

  return (
    <animated.div className={className} style={springs[type]} ref={ref}>
      {children}
    </animated.div>
  );
};

export default Spring;
